'use client';

import { Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { useGooglePlayUrl } from '@/lib/hooks/useGooglePlayUrl';
import { Icon } from '@/lib/ui-kit/icons';
import { cn } from '@/lib/ui-kit/utils';
import { Button } from '@/lib/ui-kit-v2/button';

interface DeliveryMethodCardProps {
  method: 'telegram' | 'push';
  active?: boolean;
  connected?: boolean;
  username?: string;
  onConnect?: () => void;
  isConnecting?: boolean;
  isStatusLoading?: boolean;
  /** Push: whether the user has ≥1 active push device (can't register one from web). */
  hasDevice?: boolean;
  /** Push: flip the account-level PRICE_ALERT push preference. */
  onToggle?: () => void;
  isToggling?: boolean;
}

export function DeliveryMethodCard({
  method,
  active = false,
  connected = false,
  username,
  onConnect,
  isConnecting = false,
  isStatusLoading = false,
  hasDevice = false,
  onToggle,
  isToggling = false,
}: DeliveryMethodCardProps) {
  const t = useTranslations('priceAlerts.form');
  const tCommon = useTranslations('common');
  const googlePlayUrl = useGooglePlayUrl();
  const isTelegram = method === 'telegram';

  const title = isTelegram ? t('telegram') : t('push');

  let statusText: ReactNode;
  if (!isTelegram) {
    // On/Off is already conveyed by the Enable/Disable button + active styling,
    // so the subtitle only carries the "no device" hint.
    if (isStatusLoading) {
      statusText = tCommon('loading');
    } else if (!hasDevice) {
      statusText = t.rich('pushNoDevice', {
        link: (chunks) => (
          <a href={googlePlayUrl} target="_blank" rel="noopener noreferrer" className="underline">
            {chunks}
          </a>
        ),
      });
    } else {
      statusText = '';
    }
  } else if (isStatusLoading) {
    statusText = tCommon('loading');
  } else if (!connected) {
    statusText = t('telegramDisconnected');
  } else if (username) {
    statusText = `@${username}`;
  } else {
    statusText = t('telegramConnected');
  }

  // Push toggle is available only once a mobile device is registered.
  const showPushToggle = !isTelegram && hasDevice && Boolean(onToggle);

  return (
    <div
      className={cn(
        'flex h-[60px] items-center gap-2 self-stretch rounded-[16px] p-3',
        active
          ? 'bg-v2-surface-primary shadow-sm ring-1 ring-v2-border dark:bg-v2-surface-tertiary dark:shadow-none dark:ring-0'
          : 'bg-v2-btn-secondary',
      )}
    >
      <Icon
        name={isTelegram ? 'Telegram' : 'Notify2'}
        size={24}
        className="shrink-0 text-v2-text-secondary"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span
          className={cn(
            'text-[13px] leading-4 font-[500]',
            active ? 'text-v2-text-primary' : 'text-v2-text-secondary',
          )}
        >
          {title}
        </span>
        {statusText && (
          <span className="text-[12px] leading-[115%] text-v2-text-secondary">{statusText}</span>
        )}
      </div>
      {isTelegram && !connected && onConnect && (
        <Button
          type="button"
          appearance="accent"
          size="S"
          className="shrink-0"
          onClick={onConnect}
          disabled={isConnecting || isStatusLoading}
        >
          {isConnecting || isStatusLoading ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            t('telegramConnect')
          )}
        </Button>
      )}
      {showPushToggle && (
        <Button
          type="button"
          appearance={active ? 'neutral' : 'accent'}
          size="S"
          className="shrink-0"
          onClick={onToggle}
          disabled={isToggling || isStatusLoading}
        >
          {isToggling ? (
            <Loader2 className="size-4 animate-spin" />
          ) : active ? (
            t('pushDisable')
          ) : (
            t('pushEnable')
          )}
        </Button>
      )}
    </div>
  );
}
